import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Server truth of what THIS account may see/do. Fetched from GET
// /api/auth/entitlements — never computed in the browser. Not persisted: held
// only for the session, re-fetched after a plan change and after any 402.
const EntitlementsContext = createContext(null);

const GCC = ['SA', 'AE', 'OM', 'KW', 'BH', 'QA'];

// Fail-CLOSED fallback: when we cannot fetch, we assume the account may see
// NOTHING and has NO paid features, rather than unlocking everything.
const RESTRICTED = {
  plan_id: 'free',
  plan_name: 'Free',
  plan_source: 'personal',
  preferred_country: null,
  allowed_countries: [],
  all_countries: false,
  gcc_countries: GCC,
  features: {
    all_gcc_countries: false,
    alerts: false,
    exports: false,
    create_projects_companies: false,
    create_news_tenders: false,
  },
  usage: {},
  upgrade_url: '/dashboard/settings?tab=plans',
  restricted: true,
};

export function EntitlementsProvider({ children }) {
  const { token } = useAuth();
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!token) { setEntitlements(null); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/entitlements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`entitlements ${res.status}`);
      const data = await res.json();
      setEntitlements(data);
    } catch (_) {
      // Fail closed — restrict for display rather than unlock everything.
      setEntitlements(RESTRICTED);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch once when a session token is available (and whenever it changes).
  useEffect(() => { refetch(); }, [refetch]);

  // Re-fetch after any 402 refusal (dispatched by the global fetch interceptor)
  // and after an explicit plan change.
  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener('entitlements:refetch', handler);
    return () => window.removeEventListener('entitlements:refetch', handler);
  }, [refetch]);

  return (
    <EntitlementsContext.Provider value={{ entitlements, loading, refetch }}>
      {children}
    </EntitlementsContext.Provider>
  );
}

// Returns the server's entitlements or the fail-closed RESTRICTED set while
// loading/unavailable, plus helpers. `allowedCountries` is ALWAYS an explicit
// array; an empty array means "nothing", never "everything".
export function useEntitlements() {
  const ctx = useContext(EntitlementsContext);
  const ent = ctx?.entitlements || RESTRICTED;
  return {
    entitlements: ent,
    loading: ctx?.loading || false,
    refetch: ctx?.refetch || (() => {}),
    allowedCountries: ent.allowed_countries || [],
    allCountries: !!ent.all_countries,
    gccCountries: ent.gcc_countries || GCC,
    features: ent.features || {},
    hasFeature: (f) => !!(ent.features || {})[f],
    upgradeUrl: ent.upgrade_url || '/dashboard/settings?tab=plans',
    planId: ent.plan_id || 'free',
  };
}

export default EntitlementsContext;
